/**
 * The Ages, and what it costs to reach one.
 *
 * An Age used to arrive on a wave clock: you were handed it for surviving long
 * enough. That makes it a milestone rather than a goal, and nothing downstream
 * can be built on a decision the player never made. So an Age is now bought,
 * and then *waited for*.
 *
 * Three things make that a decision rather than a purchase:
 *
 *   The cost leaves the treasury the moment you commit. There is no command to
 *   abandon a research, deliberately — the whole point is that you chose this
 *   over four companies of spears at the moment you could least spare either.
 *
 *   The research runs in real time, through sieges, and cannot be paused. You
 *   are not safe while you climb.
 *
 *   The director *knows*. Waves that land during a research are heavier, and
 *   the scouting report says so plainly, before you commit. Climbing has to
 *   cost something more interesting than resources.
 *
 * Every table here is indexed by the Age being *entered*, so `[2]` is the price
 * of leaving Ember. Indices 0 and 1 are unreachable and exist only to keep the
 * arithmetic honest.
 */

import { INDUSTRIAL_RESOURCE_KEYS, RESOURCE_KEYS, type ResourceBundle, type ResourceKey } from '../sim/types.ts';
import { isRaceCurrency, raceEconomyMask, raceReapsSouls } from './races.ts';

/**
 * Five Ages: Ember, Iron, Bell, Gilded, Hollow. The names live in `director.ts`
 * as `AGE_NAMES`.
 *
 * There were six. Dropping the last one is not just a shorter list — it moves
 * the top of the ladder from wave 60 to wave 41, which is inside the length of
 * a run people actually finish. An Age nobody reaches is content nobody sees.
 */
export const MAX_AGE = 5;

/**
 * Paid in full, up front, when the research begins.
 *
 * Priced in goods made before the destination Age. Ember establishes charcoal
 * production; later Ages require both magical refinement and manufactured
 * goods. Completed workshop requirements below keep a one-off market purchase
 * from replacing the industrial capability the next Age depends on.
 */
export const AGE_COST: ReadonlyArray<Partial<ResourceBundle>> = [
  {},
  {},
  { timber: 260, stone: 180, charcoal: 65, gold: 120 },
  /**
   * The Bell is bought with the Iron Age's own work.
   *
   * It used to ask for planks, stone and iron, which an Age-1 settlement has in
   * heaps by wave 15 — so the second Age could be skipped straight through
   * without ever running a chain built inside it. Now it asks for the three
   * refined goods, steel and tools, with gathering and charcoal still feeding
   * the workshops behind them.
   *
   * Raw `essence` is deliberately *not* on the bill: it is the intermediate,
   * spent making the other three, and charging for it twice would only mean
   * building more Wellsprings rather than more of the chain.
   */
  { magewood: 120, runestone: 90, ingots: 110, steel: 80, tools: 45, gold: 320 },
  /**
   * The Gilded is bought with the Bell's, and the rule generalises: **an Age is
   * paid for in whatever is terminal to the Age you are standing in.**
   *
   * Soulwood, voidstone, alloy and quintessence prove the magical branch, while
   * armaments, sigil frames and focusing rings prove the fabrication branch.
   * Both branches remain useful as inputs to the Gilded factories.
   *
   * Four figures of coin because by here the wage bill, not the yard, is the
   * thing that actually stops a settlement climbing.
   */
  { soulwood: 240, voidstone: 190, alloy: 210, quintessence: 200, armaments: 90, sigil_frame: 24, focusing_ring: 24, gold: 1100 },
  /**
   * The Hollow asks for ashglass, heartiron and the cores assembled from them.
   *
   * Souls are on it because the Hollow is where the settlement starts spending
   * itself, and it should be asked for a down payment before it finds that out.
   * Held under `resCap * 2` deliberately — the reliquary clamp is twice the
   * warehouse, and a price no shed could hold is a wall, not a cost.
   */
  { ashglass: 260, heartiron: 240, gilded_core: 40, souls: 550, gold: 2200 },
];

/** Finished workshops that demonstrate the current age's industrial capability. */
export const AGE_BUILDING_REQUIREMENTS: ReadonlyArray<readonly string[]> = [
  [], [], ['charcoal_kiln'], ['steelworks', 'toolsmith'],
  ['armament_workshop', 'runic_joinery', 'lens_workshop'], ['core_foundry'],
];

/**
 * How long the climb takes, in seconds of simulated time.
 *
 * Long enough that a wave will land during it — that is the entire design. A
 * research you can safely slot into a quiet build phase is just a delayed
 * purchase.
 *
 * The top two climb steeply, and on purpose. This is a grinder: the Gilded and
 * the Hollow are meant to be the shape of a whole evening rather than a step in
 * one, and the director is pressing 22% harder for every second of them. Four
 * minutes of that is a campaign in itself, which is the right weight for the
 * last door in the game.
 */
export const AGE_RESEARCH_SECONDS: readonly number[] = [0, 0, 90, 130, 240, 330];

/**
 * The wave floor: you cannot *begin* researching an Age before this wave, however
 * rich you are.
 *
 * This is what stops a rushed economy outrunning the enemy roster, and it is
 * the same shape as the table the old automatic advance used — the floor
 * survives, the automation does not.
 */
export const AGE_WAVE_FLOOR: readonly number[] = [0, 0, 6, 15, 26, 41];

/**
 * The Hearth tier an Age is carried on.
 *
 * The second floor, and the one the player pays for. `AGE_WAVE_FLOOR` says you
 * cannot climb *yet*; this says you cannot climb *here* — the fire has to be big
 * enough first, and raising it costs timber, stone and coin on the table in
 * `data/buildings.ts`.
 *
 * The Kindling asks for tier 1, which every settlement has from the first tick,
 * so the opening of a run is untouched on purpose: this bites from the Bell
 * onward, which is where the ladder is long enough for a lever to be worth
 * pulling. By the Hollow the Hearth stands at four of its five tiers and has
 * cost roughly 1,050 timber, 850 stone and 740 coin on the way — an economy's
 * worth of decision, spent on something that is also thirty-nine more beds and
 * six hundred more shed.
 */
export const AGE_HEARTH_TIER: readonly number[] = [0, 0, 1, 2, 3, 4];

/**
 * How much heavier a wave lands while you are researching.
 *
 * The window has to be genuinely dangerous or the decision is free. Visible in
 * the scouting report before the player commits, because the director's promise
 * is that everything it decides is knowable during the build phase.
 */
export const AGE_RESEARCH_THREAT = 1.22;

export function ageCost(age: number): Partial<ResourceBundle> {
  return AGE_COST[Math.max(0, Math.min(AGE_COST.length - 1, age))] ?? {};
}

export function ageResearchSeconds(age: number): number {
  return AGE_RESEARCH_SECONDS[Math.max(0, Math.min(AGE_RESEARCH_SECONDS.length - 1, age))] ?? 0;
}

export function ageWaveFloor(age: number): number {
  return AGE_WAVE_FLOOR[Math.max(0, Math.min(AGE_WAVE_FLOOR.length - 1, age))] ?? 0;
}

export function ageHearthTier(age: number): number {
  return AGE_HEARTH_TIER[Math.max(0, Math.min(AGE_HEARTH_TIER.length - 1, age))] ?? 0;
}

// ── Which resources belong to which Age ────────────────────────────────────

/**
 * The Age a resource becomes part of the economy.
 *
 * Not a gate — nothing in the simulation reads this to permit or forbid
 * anything, and a save that somehow holds essence in Age 1 is a legal save.
 * It exists so the interface can answer one question: *is this number a
 * decision the player can act on yet?*
 */
export const RESOURCE_AGE: Record<ResourceKey, number> = {
  timber: 1,
  planks: 1,
  stone: 1,
  iron: 1,
  grain: 1,
  bread: 1,
  gold: 1,
  souls: 1,
  essence: 2,
  magewood: 2,
  runestone: 2,
  ingots: 2,
  soulwood: 3,
  voidstone: 3,
  alloy: 3,
  quintessence: 3,
  ashglass: 4,
  heartiron: 4,
  hollowglass: 5,
  gravesilver: 5,
  /**
   * The race currencies are Age 1 because they are not Age-gated at all — they
   * are gated by `raceEconomyMask`, which is a different question and answered
   * in a different file. A people who has Ichor has it from the first wave.
   * `hudResources` treats them specially for exactly this reason: an Age-1 key
   * would otherwise fall off the strip at Age 3, and a currency you still spend
   * is a currency you still need to see.
   */
  ichor: 1,
  grudges: 1,
  scrap: 1,
  /**
   * The other two foods are Age 1 for the same reason the race currencies are:
   * they are not on the ladder at all. A weir and a lodge are buildable from
   * the first tick and nothing above Age 1 makes either good. What keeps them
   * off an Age-1 strip that has never fished is the `heldFood` rule in
   * `hudResources`, not this table.
   */
  fish: 1,
  meat: 1,
  charcoal: 1,
  steel: 2,
  tools: 2,
  armaments: 3,
  frame: 2,
  fitting: 2,
  sigil_frame: 3,
  focusing_ring: 3,
  gilded_core: 4,
  hollow_heart: 5,
};

/**
 * The four the strip always carries, whatever Age it is.
 *
 * Coin, because every soldier draws a wage in every Age and an empty purse is
 * how a run ends. Grain and bread, because the town eats in every Age and food
 * is the one chain the design promises will never deepen. Souls, because powers
 * cost them from wave one to the last.
 */
const ALWAYS_SHOWN: readonly ResourceKey[] = ['grain', 'bread', 'gold', 'souls'];

/**
 * Which resources the HUD strip shows at a given Age.
 *
 * The recipe ladder ends at twenty resources and a row of twenty bars is
 * not a readout, it is wallpaper. So the strip is filtered: the four above,
 * always, plus the goods of the current Age and the one below it — because the
 * Age below is exactly what the current Age's converters *eat*. At the Kindling
 * that is timber, stone and iron beside magewood, runestone and ingots, which
 * is the accumulate model made visible: the two tiers you are running at once,
 * side by side, and nothing else.
 *
 * A bar for a good you cannot yet make is noise too, so the window closes from
 * above as well: nothing from an Age you have not reached appears at all, and
 * an Age-1 settlement's strip is exactly the eight it has always been.
 *
 * Everything hidden is still held, still spent, still traded, still named in
 * every cost line and tooltip. The strip is the only thing that narrows.
 *
 * The widest the strip ever gets is the Bell: four always, four of the Kindling
 * and four of its own, which is exactly twelve. Every Age after it is *narrower*
 * — the Gilded adds two goods and the Hollow two, so the top of the ladder reads
 * cleaner than the middle of it. That is not luck; it is why those Ages were
 * given two converters apiece rather than four. Twelve is the ceiling, and the
 * Bell is standing on it, so an Age 6 must add at most as many goods as Age 5
 * has.
 */
/**
 * The race parameter is optional and the omission is deliberate: every existing
 * call site keeps working and gets exactly the strip it always got. Pass a race
 * and two things change, both of them narrowing or replacing rather than adding:
 *
 *   • a key this people cannot hold is **removed**, even from the four that are
 *     always shown — the Pale Litany bakes no bread and a bread bar pinned at
 *     zero for a whole run is the worst kind of noise, the kind that looks like
 *     a problem;
 *   • this people's **own currency is always shown**, at every Age. Race
 *     currencies carry `RESOURCE_AGE` of 1 because they are not Age-gated at
 *     all, and without this they would fall off the strip at the Bell — a
 *     currency you are still spending in the fifth Age is one you still need to
 *     see.
 *
 * **The twelve-bar ceiling moves to thirteen, and only just.** That is a real
 * change to a stated promise and it is written down rather than quietly taken:
 * the Bell was standing exactly on twelve — four always, four of the Kindling,
 * four of its own — and a people with a private currency now reads thirteen
 * there. Four races are affected, at one Age each, by one bar.
 *
 * It was allowed rather than engineered around because both alternatives were
 * worse. Hiding the race currency at the Bell alone makes a bar appear, vanish
 * and reappear across the ladder, which is the one thing a readout must never
 * do. Dropping one of the four always-shown to make room means choosing between
 * coin, grain, bread and souls on the player's behalf at exactly the Age the
 * economy is at its widest. A test pins the new ceiling at thirteen so it cannot
 * creep further, and the rule that keeps it there is `grants.length <= 1` — at
 * most one private currency per race, also asserted.
 */
export function hudResources(
  age: number,
  raceId?: string,
  soulsHeld = 0,
  heldFood: Partial<Record<'fish' | 'meat', number>> = {},
): ResourceKey[] {
  const now = Math.max(1, Math.floor(age));
  const mask = raceId ? raceEconomyMask(raceId, RESOURCE_KEYS) : undefined;
  /**
   * Souls are universal — ranks, the hero's revival, reforging and half the
   * arcane catalogue charge them, whoever you are — but *holding* them is not.
   * A reaper's dead pay in souls from the first skirmish; everyone else starts
   * at zero with no producer, and a chip that reads 0 for the first hour of a
   * run is noise wearing a number. So the chip earns its place: shown from the
   * start for a race whose ground yields them, and for the rest the moment the
   * settlement holds any or the second Age opens the soul-economy buildings.
   */
  const soulsLive = raceId
    ? raceReapsSouls(raceId) || soulsHeld >= 1 || now >= 2
    : soulsHeld >= 1 || now >= 2;
  return RESOURCE_KEYS.filter((k) => {
    // Workshop inventories remain visible in Industry & Ages and all cost
    // tooltips; ten extra chips would obscure food and the current age's stock.
    if (INDUSTRIAL_RESOURCE_KEYS.includes(k)) return false;
    if (k === 'souls' && !soulsLive) return false;
    /**
     * Fish and salt meat, on the souls rule and for the souls reason.
     *
     * They are food, so they belong beside grain and bread wherever the player
     * reads food — but a bar pinned at zero for a whole run is noise wearing a
     * number, and a settlement with no river in reach may never build a weir.
     * So the chip earns its place by being *held*: the moment the first basket
     * lands, the bar is there for the rest of the run, at every Age.
     *
     * **This moves the stated ceiling from thirteen to fifteen**, and it is
     * written down rather than quietly taken. The Bell was standing exactly on
     * twelve, a private currency made thirteen, and a settlement that fishes
     * *and* hunts reads fifteen there. It was allowed because the alternatives
     * are worse in the way this file already argues once: hiding a food chip at
     * one Age makes a bar appear, vanish and reappear across the ladder, and
     * dropping one of the four always-shown means choosing between coin, grain,
     * bread and souls on the player's behalf. Two of the fifteen are there
     * because the player built a building to put them there.
     */
    if (k === 'fish' || k === 'meat') return (heldFood[k] ?? 0) > 0;
    if (isRaceCurrency(k)) {
      /**
       * A private currency belongs to somebody or to nobody, and there is no
       * middle. Called without a race — which every pre-race call site does, and
       * which the Age tests do — the honest answer is that *this* settlement has
       * none of them, because a settlement with no race named is the generic one
       * and the generic one holds only the common list. Answering "all of them"
       * would have put Ichor, Grudges and Scrap on an Age-1 Ashen strip, which
       * is how this was caught.
       */
      return mask ? mask.has(k) : false;
    }
    if (mask && !mask.has(k)) return false;
    return ALWAYS_SHOWN.includes(k) || (RESOURCE_AGE[k] <= now && RESOURCE_AGE[k] >= now - 1);
  });
}
